import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { PageHeader } from '../../components/PageHeader.jsx';
import { Modal, ConfirmDialog } from '../../components/Modal.jsx';
import { FormField } from '../../components/FormField.jsx';

const chipFor = {
  new: 'chip-warn',
  replied: 'chip-success',
  archived: 'chip-neutral',
};

export default function ContactMessages() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('new');
  const [viewing, setViewing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');

  async function refresh() {
    const r = await Admin.contactMessages.list(filter || undefined);
    setItems(r.messages);
  }
  useEffect(() => { refresh(); }, [filter]);

  function open(m) {
    setViewing(m);
    setNotes(m.adminNotes || '');
  }

  async function saveNotes() {
    if (!viewing) return;
    setBusy(true);
    try {
      await Admin.contactMessages.update(viewing.id, { adminNotes: notes });
      setViewing((v) => ({ ...v, adminNotes: notes }));
    } finally {
      setBusy(false);
    }
  }

  async function markReplied(id) {
    await Admin.contactMessages.markReplied(id);
    if (viewing?.id === id) setViewing((v) => ({ ...v, status: 'replied' }));
    await refresh();
  }
  async function archive(id) {
    await Admin.contactMessages.archive(id);
    if (viewing?.id === id) setViewing((v) => ({ ...v, status: 'archived' }));
    await refresh();
  }
  async function remove(id) {
    await Admin.contactMessages.remove(id);
    if (viewing?.id === id) setViewing(null);
    await refresh();
  }

  return (
    <div>
      <PageHeader
        eyebrow="Inbox"
        title="Contact inquiries"
        description="Messages sent via the public contact form."
      />
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <select className="input max-w-xs" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All</option>
          <option value="new">New</option>
          <option value="replied">Replied</option>
          <option value="archived">Archived</option>
        </select>
        <span className="text-xs text-ink-3">{items.length} {items.length === 1 ? 'message' : 'messages'}</span>
      </div>

      {items.length === 0 ? (
        <div className="card text-center py-12 text-ink-3 italic font-display">No messages.</div>
      ) : (
        <div className="space-y-3">
          {items.map((m) => (
            <article
              key={m.id}
              className="card hover:border-ink/30 transition-colors cursor-pointer"
              onClick={() => open(m)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-display text-xl truncate">{m.subject}</h3>
                    <span className={chipFor[m.status] || 'chip-neutral'}>{m.status}</span>
                  </div>
                  <div className="text-sm text-ink-2">
                    {m.name} · {m.email}{m.phone ? ` · ${m.phone}` : ''}
                  </div>
                  <p className="text-sm text-ink-3 mt-2 line-clamp-2 whitespace-pre-wrap">{m.message}</p>
                  {m.interests?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {m.interests.map((tag) => (
                        <span key={tag} className="chip-neutral">{tag}</span>
                      ))}
                    </div>
                  )}
                  <div className="text-[11px] text-ink-3 mt-2">
                    {new Date(m.createdAt).toLocaleString()}
                  </div>
                </div>
                <span className="text-ink-3 shrink-0">→</span>
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal
        open={!!viewing}
        onClose={() => setViewing(null)}
        eyebrow={viewing?.status || 'Message'}
        title={viewing?.subject || ''}
        size="lg"
        footer={
          viewing && (
            <>
              <button
                onClick={() => setConfirm({ kind: 'delete', m: viewing })}
                className="btn-ghost text-danger mr-auto"
              >
                Delete
              </button>
              {viewing.status !== 'archived' && (
                <button onClick={() => archive(viewing.id)} className="btn-outline">
                  Archive
                </button>
              )}
              {viewing.status !== 'replied' && (
                <button onClick={() => markReplied(viewing.id)} className="btn-primary">
                  Mark replied
                </button>
              )}
              {viewing.email && (
                <a
                  href={`mailto:${viewing.email}?subject=${encodeURIComponent('Re: ' + viewing.subject)}`}
                  className="btn-secondary"
                >
                  Reply by email
                </a>
              )}
            </>
          )
        }
      >
        {viewing && (
          <div className="space-y-4">
            <Row label="From" value={
              <>
                <div>{viewing.name}</div>
                <div className="text-ink-2 text-sm">
                  <a href={`mailto:${viewing.email}`} className="text-brand underline underline-offset-4">
                    {viewing.email}
                  </a>
                  {viewing.phone && <> · {viewing.phone}</>}
                </div>
              </>
            } />
            {viewing.interests?.length > 0 && (
              <Row label="Interested in" value={
                <div className="flex flex-wrap gap-1">
                  {viewing.interests.map((tag) => <span key={tag} className="chip-brand">{tag}</span>)}
                </div>
              } />
            )}
            <Row label="Received" value={new Date(viewing.createdAt).toLocaleString()} />
            <div className="border-t border-line pt-4">
              <div className="eyebrow mb-2">Message</div>
              <p className="text-ink whitespace-pre-wrap leading-relaxed">{viewing.message}</p>
            </div>
            <div className="border-t border-line pt-4">
              <FormField label="Internal notes" hint="Visible to admins only">
                <textarea
                  className="input"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={saveNotes}
                  placeholder="Add a note about how this was handled"
                />
              </FormField>
              {busy && <div className="text-[11px] text-ink-3">Saving…</div>}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={confirm?.kind === 'delete'}
        onClose={() => setConfirm(null)}
        onConfirm={() => remove(confirm.m.id)}
        title={`Delete message from ${confirm?.m?.name}?`}
        message="This removes the inquiry entirely. Use Archive instead to keep a record."
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-4 items-start">
      <dt className="eyebrow pt-1">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}
